import dayjs from 'dayjs';

import {
  EPlanOrderStatus,
  EPlanQuantitySuffix,
  EPlanFormulationProductType,
  EPlanVehicleSize,
  EPlanMaterialType,
  EPlanPlacingMethod,
  EPlanOrderStatusLabel,
  EPlanQuantitySuffixLabel,
  EPlanFormulationProductTypeLabel,
  EPlanVehicleSizeLabel,
  EPlanMaterialTypeLabel,
  EPlanPlacingMethodLabel,
} from '../types/plan.enum';

/**
 * Helper functions to convert enum values to display labels
 */
export const enumToLabel = {
  // Order Status (column 2)
  orderStatus: (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    switch (value) {
      case EPlanOrderStatus.NOT_YET:
        return '交渉中';
      case EPlanOrderStatus.DONE:
        return '手配済';
      default:
        return '';
    }
  },
  // Quantity Suffix (column 10)
  quantitySuffix: (value: number | null): string => {
    switch (value) {
      case EPlanQuantitySuffix.OUT:
        return '出';
      case EPlanQuantitySuffix.WAIT:
        return '待';
      default:
        return '';
    }
  },
  // Formulation Product Type (column 12)
  formulationProductType: (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    switch (value) {
      case EPlanFormulationProductType.CONCRETE:
        return '生コン';
      case EPlanFormulationProductType.OTHER:
        return '他商品';
      default:
        return '';
    }
  },
  // Vehicle Size (column 14)
  vehicleSize: (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    switch (value) {
      case EPlanVehicleSize.LARGE:
        return '大型';
      case EPlanVehicleSize.MEDIUM:
        return '中型';
      case EPlanVehicleSize.SMALL:
        return '小型';
      case EPlanVehicleSize.METER:
        return 'm³車';
      case EPlanVehicleSize.PICKUP:
        return '引取';
      default:
        return '';
    }
  },
  // Material Type (column 15)
  materialType: (value: number | null): string => {
    switch (value) {
      case EPlanMaterialType.NO:
        return 'なし';
      case EPlanMaterialType.BAG_CEMENT:
        return '袋セメント';
      case EPlanMaterialType.MORTAR_1_1:
        return '1:1モルタル';
      case EPlanMaterialType.MORTAR_1_2:
        return '1:2モルタル';
      case EPlanMaterialType.MORTAR_1_3:
        return '1:3モルタル';
      case EPlanMaterialType.MORTAR_1_4:
        return '1:4モルタル';
      case EPlanMaterialType.SPECIAL_MORTAR:
        return '特殊モルタル';
      default:
        return '';
    }
  },
  // Placing Method (column 19)
  placingMethod: (value: number | null): string => {
    switch (value) {
      case EPlanPlacingMethod.PUMP_1:
        return 'ポンプ1基';
      case EPlanPlacingMethod.PUMP_2:
        return 'ポンプ2基';
      case EPlanPlacingMethod.BUCKET:
        return 'バケット';
      case EPlanPlacingMethod.CAT:
        return 'ネコ';
      default:
        return '';
    }
  },
};

/** Display text for placing method; uses placingMethodOthers when method is OTHER. */
export const getPlacingMethodDisplayText = (
  placingMethod: number | string | null | undefined,
  placingMethodOthers?: string | null
): string => {
  if (placingMethod === null || placingMethod === undefined || placingMethod === '') {
    return '';
  }
  const placingMethodNum = Number(placingMethod);
  if (placingMethodNum === EPlanPlacingMethod.OTHER) {
    return placingMethodOthers || '';
  }
  return enumToLabel.placingMethod(placingMethodNum);
};

/**
 * Helper functions to convert display labels back to enum values
 */
export const labelToEnum = {
  // Order Status (column 2)
  orderStatus: (label: string): number | null => {
    const trimmed = (label ?? '').toString().trim();
    if (!trimmed) return null;
    switch (trimmed) {
      case EPlanOrderStatusLabel.NOT_YET:
        return EPlanOrderStatus.NOT_YET;
      case EPlanOrderStatusLabel.DONE:
        return EPlanOrderStatus.DONE;
      default:
        return null;
    }
  },
  // Quantity Suffix (column 10)
  quantitySuffix: (label: string): number | null => {
    switch (label) {
      case EPlanQuantitySuffixLabel.OUT:
        return EPlanQuantitySuffix.OUT;
      case EPlanQuantitySuffixLabel.WAIT:
        return EPlanQuantitySuffix.WAIT;
      default:
        return null;
    }
  },
  // Formulation Product Type (column 12)
  formulationProductType: (label: string): number | null => {
    const trimmed = (label ?? '').toString().trim();
    if (!trimmed) return null;
    switch (trimmed) {
      case EPlanFormulationProductTypeLabel.CONCRETE:
        return EPlanFormulationProductType.CONCRETE;
      case EPlanFormulationProductTypeLabel.OTHER:
        return EPlanFormulationProductType.OTHER;
      default:
        return null;
    }
  },
  // Vehicle Size (column 14)
  vehicleSize: (label: string): number | null => {
    const trimmed = (label ?? '').toString().trim();
    if (!trimmed) return null;
    switch (trimmed) {
      case EPlanVehicleSizeLabel.LARGE:
        return EPlanVehicleSize.LARGE;
      case EPlanVehicleSizeLabel.MEDIUM:
        return EPlanVehicleSize.MEDIUM;
      case EPlanVehicleSizeLabel.SMALL:
        return EPlanVehicleSize.SMALL;
      case EPlanVehicleSizeLabel.METER:
        return EPlanVehicleSize.METER;
      case EPlanVehicleSizeLabel.PICKUP:
        return EPlanVehicleSize.PICKUP;
      default:
        return null;
    }
  },
  // Material Type (column 15)
  materialType: (label: string): number | null => {
    switch (label) {
      case EPlanMaterialTypeLabel.NO:
        return EPlanMaterialType.NO;
      case EPlanMaterialTypeLabel.BAG_CEMENT:
        return EPlanMaterialType.BAG_CEMENT;
      case EPlanMaterialTypeLabel.MORTAR_1_1:
        return EPlanMaterialType.MORTAR_1_1;
      case EPlanMaterialTypeLabel.MORTAR_1_2:
        return EPlanMaterialType.MORTAR_1_2;
      case EPlanMaterialTypeLabel.MORTAR_1_3:
        return EPlanMaterialType.MORTAR_1_3;
      case EPlanMaterialTypeLabel.MORTAR_1_4:
        return EPlanMaterialType.MORTAR_1_4;
      case EPlanMaterialTypeLabel.SPECIAL_MORTAR:
        return EPlanMaterialType.SPECIAL_MORTAR;
      default:
        return null;
    }
  },
  // Placing Method (column 19)
  placingMethod: (label: string): number | null => {
    switch (label) {
      case EPlanPlacingMethodLabel.PUMP_1:
        return EPlanPlacingMethod.PUMP_1;
      case EPlanPlacingMethodLabel.PUMP_2:
        return EPlanPlacingMethod.PUMP_2;
      case EPlanPlacingMethodLabel.BUCKET:
        return EPlanPlacingMethod.BUCKET;
      case EPlanPlacingMethodLabel.CAT:
        return EPlanPlacingMethod.CAT;
      default:
        return null;
    }
  },
};

/**
 * Map table cell text to API fields: known labels → enum 1–4 and clear others;
 * any other non-empty text → OTHER (5) with the trimmed text in placingMethodOthers.
 */
export function parsePlacingMethodFromCell(raw: string | null | undefined): {
  placingMethod: number | null;
  placingMethodOthers: string | null;
} {
  if (raw === '' || raw === null || raw === undefined) {
    return { placingMethod: null, placingMethodOthers: null };
  }
  const trimmed = String(raw).trim();
  if (trimmed === '') {
    return { placingMethod: null, placingMethodOthers: null };
  }
  const known = labelToEnum.placingMethod(trimmed);
  if (known !== null) {
    return { placingMethod: known, placingMethodOthers: null };
  }
  return {
    placingMethod: EPlanPlacingMethod.OTHER,
    placingMethodOthers: trimmed,
  };
}

export function hourMinuteValidatorAllowEmpty(value: string, callback: (valid: boolean) => void) {
  const regexTime = /^([01]\d|2\d):[0-5]\d$/;

  if ((value && regexTime.test(value)) || !value) {
    callback(true);
    return;
  }
  callback(false);
}

/**
 * Validate date format: YY/MM/DD
 * Min year: 2010, 2010/01/01
 * If not valid don't let user modify the cell or leave the cell empty
 * Empty value is not allowed - user must provide a valid date
 * @param value - Date string in YY/MM/DD format
 * @param callback - Callback function with validation result
 */
export function dateValidator(value: string, callback: (valid: boolean) => void) {
  // Reject empty value - date field is required
  if (!value || value.trim() === '') {
    callback(false);
    return;
  }

  const trimmedValue = value.trim();

  // Check format: YY/MM/DD
  const regexDate = /^([0-9]{2})\/([0-9]{2})\/([0-9]{2})$/;
  if (!regexDate.test(trimmedValue)) {
    callback(false);
    return;
  }

  // Parse date parts
  const parts = trimmedValue.split('/');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Validate month (01-12)
  if (month < 1 || month > 12) {
    callback(false);
    return;
  }

  // Validate day (01-31, and must be valid for the month)
  if (day < 1 || day > 31) {
    callback(false);
    return;
  }

  // Convert 2-digit year to 4-digit (always 20XX)
  // Mặc định 2 số phía trước của năm là 20, nên không cần check
  const fullYear = 2000 + year;

  // Check min year: >= 2010 (10/01/01)
  if (fullYear < 2010) {
    callback(false);
    return;
  }

  // Validate date using dayjs (checks if date is valid, e.g., 31/02/2024 is invalid)
  const dateStr = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const date = dayjs(dateStr);

  if (!date.isValid()) {
    callback(false);
    return;
  }

  // Check if parsed date matches input (prevents invalid dates like 31/02/2024)
  const parsedYear = date.year();
  const parsedMonth = date.month() + 1;
  const parsedDay = date.date();

  if (parsedYear !== fullYear || parsedMonth !== month || parsedDay !== day) {
    callback(false);
    return;
  }

  // All validations passed
  callback(true);
}
